import type { ApsOcrEnumOption } from '../types/apsOcr';
import type {
    QuestionOption,
    QuestionSchema,
    QuestionType,
    SelectOption,
    TemplateFormFieldUpdate,
} from '../types/templateFormFields';
import {
    getIntakePrefillTag,
    getMatterFieldOptions,
    getMatterFieldQuestionType,
    INTAKE_FIELD_PREFILL_TAGS,
    isIntakeTagForMatterType,
    type IntakePrefillTag,
} from './matterFields';

export interface ApsPrefillMappableField {
    templateFormFieldId: string;
    name: string;
    title: string;
    formGroupTitle: string | null;
    questionSchema: QuestionSchema;
}

export type ApsPrefillMappedBy = 'name' | 'tag' | 'both';

export interface ApsPrefillMappedRow {
    templateFormFieldId: string;
    questionLabel: string;
    questionName: string;
    tag: string;
    tagLabel: string;
    exampleValue: string;
    mappedBy: ApsPrefillMappedBy;
}

export interface ApsPrefillTagSuggestion {
    templateFormFieldId: string;
    questionLabel: string;
    questionName: string;
    tag: string;
    tagLabel: string;
    exampleValue: string;
}

export interface ApsPrefillUnmappedTag {
    tag: string;
    tagLabel: string;
    exampleValue: string;
    recommendedLabel: string;
    questionType: QuestionType;
    questionTypeLabel: string;
}

/** Questions must have the question type of the tag's field to match. */
export interface ApsPrefillTagMappingRule {
    questionNames: string[];
    tag: string;
    requiredTitle?: string;
    requirePropertyDetailsGroup?: boolean;
}

const PROPERTY_DETAILS_GROUP = 'property details';

const RENTAL_ITEMS_TITLE =
    'List all rental items in your property (e.g., hot water tank, alarm system, propane tanks, furnace, air conditioning unit, water softener, etc.)';

/**
 * Matter-specific mappings from docs/aps-ocr-intake-field-mapping.md,
 * matching backend/db/migrations/0080 and 0081.
 *
 * Title is an additional match key so copied templates with regenerated
 * question names still map when the label is unchanged.
 */
export const PURCHASE_APS_PREFILL_TAG_RULES: ApsPrefillTagMappingRule[] = [
    {
        questionNames: ['closingDate'],
        tag: 'closingDate',
        requiredTitle: 'Closing date listed on your purchase and sale agreement',
    },
    {
        questionNames: ['purchasersListedOnPurchaseAndSaleAgreement'],
        tag: 'purchasersListedOnPurchaseAndSaleAgreement',
        requiredTitle: 'Number of purchasers listed on the purchase and sale agreement',
    },
    {
        questionNames: ['subjectPropertyAddress'],
        tag: 'subjectPropertyAddress',
        requiredTitle: 'Enter subject property address',
    },
    {
        questionNames: ['purchasePrice'],
        tag: 'purchasePrice',
        requiredTitle: 'Enter your purchase price',
    },
    {
        questionNames: ['unitNumber'],
        tag: 'unitNumber',
        requirePropertyDetailsGroup: true,
    },
    {
        questionNames: ['text_13723131', 'rentalItems'],
        tag: 'rentalItems',
        requiredTitle: RENTAL_ITEMS_TITLE,
    },
    {
        questionNames: ['propertyType'],
        tag: 'propertyType',
        requiredTitle: 'Select the type of property',
    },
    {
        questionNames: ['text_93800087', 'coOpBrokerage'],
        tag: 'coOpBrokerage',
        requiredTitle: 'What Real Estate Broker are you using?',
    },
    {
        questionNames: ['text_13237129', 'buyerAgentName'],
        tag: 'buyerAgentName',
        requiredTitle: 'Real estate agent name',
    },
];

export const SALE_APS_PREFILL_TAG_RULES: ApsPrefillTagMappingRule[] = [
    {
        questionNames: ['closingDate'],
        tag: 'closingDate',
        requiredTitle: 'Closing date listed on your purchase and sale agreement',
    },
    {
        questionNames: ['sellersListedOnPurchaseAndSaleAgreement'],
        tag: 'sellersListedOnPurchaseAndSaleAgreement',
        requiredTitle: 'Number of sellers listed on the purchase and sale agreement',
    },
    {
        questionNames: ['subjectPropertyAddress'],
        tag: 'subjectPropertyAddress',
        requiredTitle: 'Enter subject property address',
    },
    {
        questionNames: ['salePrice'],
        tag: 'salePrice',
        requiredTitle: 'Enter your sale price',
    },
    {
        questionNames: ['unitNumber'],
        tag: 'unitNumber',
        requirePropertyDetailsGroup: true,
    },
    {
        questionNames: ['rentalItems'],
        tag: 'rentalItems',
        requiredTitle: RENTAL_ITEMS_TITLE,
    },
    {
        questionNames: ['propertyType'],
        tag: 'propertyType',
        requiredTitle: 'Select the type of property',
    },
    {
        questionNames: ['text_89521325', 'hasRealEstateAgent'],
        tag: 'hasRealEstateAgent',
        requiredTitle: 'Is this sale being handled through a real estate agent?',
    },
    {
        questionNames: ['text_38340428', 'listingBrokerage'],
        tag: 'listingBrokerage',
        requiredTitle: 'Real estate company name',
    },
    {
        questionNames: ['text_51039257', 'sellerAgentName'],
        tag: 'sellerAgentName',
        requiredTitle: 'Real estate agent name',
    },
    {
        questionNames: ['text_48441699', 'text_48155291', 'commonExpensesApprox'],
        tag: 'commonExpensesApprox',
        requiredTitle: 'Amount of Monthly Common Expenses',
    },
];

function isMatterExcludedTag(intakeTag: IntakePrefillTag, matterType: string): boolean {
    return !isIntakeTagForMatterType(intakeTag, matterType);
}

function getQuestionType(intakeTag: IntakePrefillTag): QuestionType {
    return getMatterFieldQuestionType(intakeTag.field);
}

function normalizeTitle(value: string): string {
    return value.trim().replace(/:+$/u, '').replace(/\s+/gu, ' ').toLowerCase();
}

function namesMatch(field: ApsPrefillMappableField, questionNames: string[]): boolean {
    if (!questionNames.includes(field.name)) return false;
    const schemaName = field.questionSchema.name;
    return !schemaName || questionNames.includes(schemaName);
}

function titlesMatch(field: ApsPrefillMappableField, requiredTitle: string): boolean {
    const expected = normalizeTitle(requiredTitle);
    if (!expected) return false;

    return [field.title, field.questionSchema.label].some(
        (value) =>
            typeof value === 'string' && value.trim() !== '' && normalizeTitle(value) === expected,
    );
}

function isPropertyDetailsGroup(field: ApsPrefillMappableField): boolean {
    const group = field.formGroupTitle ?? field.questionSchema.formGroupTitle ?? '';
    return normalizeTitle(group) === PROPERTY_DETAILS_GROUP;
}

function tagsAreUsable(field: ApsPrefillMappableField): boolean {
    const { tags } = field.questionSchema;
    return tags == null || Array.isArray(tags);
}

function fieldAlreadyHasTag(field: ApsPrefillMappableField, tag: string): boolean {
    return Array.isArray(field.questionSchema.tags) && field.questionSchema.tags.includes(tag);
}

function fieldQuestionLabel(field: ApsPrefillMappableField): string {
    const label = field.questionSchema.label?.trim();
    if (label) return label;
    return field.title;
}

function fieldMatchesRule(field: ApsPrefillMappableField, rule: ApsPrefillTagMappingRule): boolean {
    const intakeTag = getIntakePrefillTag(rule.tag);
    if (!intakeTag) return false;
    if (!field.questionSchema || typeof field.questionSchema !== 'object') return false;
    if (field.questionSchema.type !== getQuestionType(intakeTag)) return false;
    if (!tagsAreUsable(field)) return false;
    if (rule.requirePropertyDetailsGroup && !isPropertyDetailsGroup(field)) return false;

    const nameMatched = namesMatch(field, rule.questionNames);
    if (!rule.requiredTitle) return nameMatched;

    return nameMatched || titlesMatch(field, rule.requiredTitle);
}

function rulesForMatterType(matterType: string): ApsPrefillTagMappingRule[] {
    if (matterType === 'Purchase') return PURCHASE_APS_PREFILL_TAG_RULES;
    if (matterType === 'Sale') return SALE_APS_PREFILL_TAG_RULES;
    return [];
}

function catalogFallbackRules(matterType: string): ApsPrefillTagMappingRule[] {
    return INTAKE_FIELD_PREFILL_TAGS.flatMap((intakeTag) => {
        if (isMatterExcludedTag(intakeTag, matterType)) return [];
        return [
            {
                questionNames: [intakeTag.tag],
                tag: intakeTag.tag,
                requirePropertyDetailsGroup: intakeTag.tag === 'unitNumber',
            },
        ];
    });
}

function toSuggestion(
    field: ApsPrefillMappableField,
    intakeTag: IntakePrefillTag,
): ApsPrefillTagSuggestion {
    return {
        templateFormFieldId: field.templateFormFieldId,
        questionLabel: fieldQuestionLabel(field),
        questionName: field.name,
        tag: intakeTag.tag,
        tagLabel: intakeTag.label,
        exampleValue: intakeTag.example,
    };
}

function collectSuggestions(
    fields: ApsPrefillMappableField[],
    rules: ApsPrefillTagMappingRule[],
    seen: Set<string>,
): ApsPrefillTagSuggestion[] {
    const suggestions: ApsPrefillTagSuggestion[] = [];

    for (const rule of rules) {
        const intakeTag = getIntakePrefillTag(rule.tag);
        if (!intakeTag) continue;

        for (const field of fields) {
            if (!fieldMatchesRule(field, rule)) continue;
            if (fieldAlreadyHasTag(field, rule.tag)) continue;

            const key = `${field.templateFormFieldId}:${rule.tag}`;
            if (seen.has(key)) continue;
            seen.add(key);
            suggestions.push(toSuggestion(field, intakeTag));
        }
    }

    return suggestions;
}

function fieldNameMatchesTag(field: ApsPrefillMappableField, tag: string): boolean {
    return field.name === tag || field.questionSchema.name === tag;
}

function collectAlreadyMapped(
    fields: ApsPrefillMappableField[],
    matterType: string,
): ApsPrefillMappedRow[] {
    const rows: ApsPrefillMappedRow[] = [];
    const seen = new Set<string>();

    for (const intakeTag of INTAKE_FIELD_PREFILL_TAGS) {
        if (isMatterExcludedTag(intakeTag, matterType)) continue;
        const { tag } = intakeTag;

        for (const field of fields) {
            if (!field.questionSchema || typeof field.questionSchema !== 'object') continue;
            if (!tagsAreUsable(field)) continue;

            const byTag = fieldAlreadyHasTag(field, tag);
            let byName = fieldNameMatchesTag(field, tag);
            if (tag === 'unitNumber' && byName && !isPropertyDetailsGroup(field)) {
                byName = false;
            }
            if (!byTag && !byName) continue;

            const key = `${field.templateFormFieldId}:${tag}`;
            if (seen.has(key)) continue;
            seen.add(key);

            rows.push({
                ...toSuggestion(field, intakeTag),
                mappedBy: byName && byTag ? 'both' : byTag ? 'tag' : 'name',
            });
        }
    }

    return rows;
}

export function getApsPrefillQuestionTypeLabel(type: QuestionType): string {
    switch (type) {
        case 'address':
            return 'Address';
        case 'currency':
            return 'Currency';
        case 'date':
            return 'Date';
        case 'radio':
            return 'Radio';
        case 'select':
            return 'Select';
        case 'text':
            return 'Text';
        case 'textarea':
            return 'Text Area';
        default:
            return type;
    }
}

export function getApsPrefillRecommendedQuestionLabel(tag: string, matterType: string): string {
    const intakeTag = getIntakePrefillTag(tag);
    if (!intakeTag) return tag;

    const ruleTitle = rulesForMatterType(matterType).find(
        (rule) => rule.tag === tag && rule.requiredTitle,
    )?.requiredTitle;
    return ruleTitle ?? intakeTag.question;
}

export function getApsPrefillTagExampleValue(tag: string): string {
    return getIntakePrefillTag(tag)?.example ?? '';
}

function toSelectOptions(options: ApsOcrEnumOption[]): SelectOption[] {
    return options.map((option) => ({
        selectItemKey: option.value,
        selectItemLabel: option.label,
        selectItemValue: option.value,
    }));
}

function toRadioOptions(options: ApsOcrEnumOption[]): QuestionOption[] {
    return options.map((option) => ({ optionLabel: option.label, optionValue: option.value }));
}

export interface ApsPrefillPresetFieldInput {
    name: string;
    formGroup?: number;
    formGroupTitle?: string;
    displayOrder: number;
}

export function toApsPrefillMappableFields(
    fields: Array<{
        templateFormFieldId?: string;
        name: string;
        title: string;
        formGroupTitle?: string | null;
        questionSchema: QuestionSchema;
    }>,
): ApsPrefillMappableField[] {
    return fields.map((field) => ({
        templateFormFieldId: field.templateFormFieldId ?? field.name,
        name: field.name,
        title: field.title,
        formGroupTitle: field.formGroupTitle ?? field.questionSchema.formGroupTitle ?? null,
        questionSchema: field.questionSchema,
    }));
}

export function buildApsPrefillPresetField(
    preset: ApsPrefillUnmappedTag,
    input: ApsPrefillPresetFieldInput,
): TemplateFormFieldUpdate {
    const questionSchema: QuestionSchema = {
        name: input.name,
        type: preset.questionType,
        label: preset.recommendedLabel,
        order: input.displayOrder,
        value: '',
        placeholder: '',
        formGroupTitle: input.formGroupTitle,
        isCustomQuestion: true,
        conditions: [],
        tags: [preset.tag],
        ...(input.formGroup != null ? { formGroup: input.formGroup } : {}),
    } as QuestionSchema;

    const intakeTag = getIntakePrefillTag(preset.tag);
    const options = intakeTag ? getMatterFieldOptions(intakeTag.field) : undefined;

    if (preset.questionType === 'radio') {
        questionSchema.options = toRadioOptions(options ?? []);
        questionSchema.radioOrientation = 'horizontal';
    }

    if (preset.questionType === 'select') {
        questionSchema.selectOptions = toSelectOptions(options ?? []);
    }

    return {
        name: input.name,
        title: preset.recommendedLabel,
        subTitle: '',
        displayOrder: input.displayOrder,
        isVisibleToUser: true,
        isLocked: false,
        formGroupTitle: input.formGroupTitle ?? '',
        questionSchema,
        questionValidationSchema: {},
    };
}

export function appendPrefillTag(questionSchema: QuestionSchema, tag: string): QuestionSchema {
    const tags = Array.isArray(questionSchema.tags) ? questionSchema.tags : [];
    if (tags.includes(tag)) return questionSchema;
    return {
        ...questionSchema,
        tags: [...tags, tag],
    };
}

export function suggestApsPrefillTagMappings(
    fields: ApsPrefillMappableField[],
    matterType: string,
): {
    alreadyMapped: ApsPrefillMappedRow[];
    recommended: ApsPrefillTagSuggestion[];
    unmappedTags: ApsPrefillUnmappedTag[];
} {
    const seen = new Set<string>();
    const recommended = [
        ...collectSuggestions(fields, rulesForMatterType(matterType), seen),
        ...collectSuggestions(fields, catalogFallbackRules(matterType), seen),
    ];
    const alreadyMapped = collectAlreadyMapped(fields, matterType);

    const presentTags = new Set<string>();
    for (const row of alreadyMapped) {
        presentTags.add(row.tag);
    }
    for (const row of recommended) {
        presentTags.add(row.tag);
    }

    const unmappedTags = INTAKE_FIELD_PREFILL_TAGS.flatMap((intakeTag) => {
        if (presentTags.has(intakeTag.tag)) return [];
        if (isMatterExcludedTag(intakeTag, matterType)) return [];
        const questionType = getQuestionType(intakeTag);
        return [
            {
                tag: intakeTag.tag,
                tagLabel: intakeTag.label,
                exampleValue: intakeTag.example,
                recommendedLabel: getApsPrefillRecommendedQuestionLabel(intakeTag.tag, matterType),
                questionType,
                questionTypeLabel: getApsPrefillQuestionTypeLabel(questionType),
            },
        ];
    });

    return { alreadyMapped, recommended, unmappedTags };
}
